import { ChangeEvent, Fragment, useEffect, useState } from "react"

import { GeneralMbaConfig, MbaConfiguration } from "models/mba"
import { css, cx } from "@emotion/css"

import { ColorPickerField } from "components/inputs/ColorPickerField"
import Options from "components/inputs/Options"
import { useI18n } from "providers/I18n"

type Props = {
  data?: GeneralMbaConfig
  update: (
    variables: MbaConfiguration & { clientConfig?: string },
  ) => Promise<MbaConfiguration> | void
  onChange?: ({ name, value }: { name: string; value?: string }) => void
  id?: string
  viewIntermediary?: boolean
  disabled?: boolean
  configScope?: "CAMPAIGN" | "CLIENT_AGENCY"
}

export function GeneralSettings(props: Props) {
  const { t } = useI18n()
  const { update, id, data, viewIntermediary, disabled, configScope } = props
  const viewIntermediaryString =
    viewIntermediary !== undefined ? String(viewIntermediary) : "false"
  const [open, handlePicker] = useState(false)
  const [value, setValue] = useState<Partial<GeneralMbaConfig>>(data ?? {})
  const [color, setColor] = useState<string>("")
  const [vendor, setVendor] = useState<string>(viewIntermediaryString)

  const fontData = [
    { label: "Calibri", value: "CALIBRI" },
    { label: "Arial", value: "ARIAL" },
    { label: "Comic Sans MS", value: "COMIC_SANS_MS" },
  ]

  const intermediaryData = [
    { label: t("intermediaryData.show_vendors_only"), value: "false" },
    { label: t("intermediaryData.show_mo_and_vendor"), value: "true" },
  ]

  const fontSizeData = [
    { label: "10", value: "10" },
    { label: "11", value: "11" },
    { label: "12", value: "12" },
    { label: "14", value: "14" },
    { label: "16", value: "16" },
    { label: "18", value: "18" },
    { label: "20", value: "20" },
    { label: "22", value: "22" },
    { label: "24", value: "24" },
  ]

  const borderData = [
    { label: t("borderData.thin"), value: "THIN" },
    { label: t("borderData.medium"), value: "MEDIUM" },
    { label: t("borderData.thick"), value: "THICK" },
  ]

  const inputs = [
    { name: "font", options: fontData } /* t("mba.general-settings.font") */,
    {
      name: "fontSize",
      options: fontSizeData,
    } /* t("mba.general-settings.fontSize") */,
    {
      name: "borderColor",
      options: [],
    } /* t("mba.general-settings.borderColor") */,
    {
      name: "borderStyle",
      options: borderData,
    } /* "mba.general-settings.borderStyle" */,
  ]

  const handleOpen = (arg: boolean) => {
    handlePicker(arg)
  }
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue({
      ...value,
      [event.target.name]: event.target.value,
    })
    event.target.value &&
      update({
        general: {
          [event.target.name]: event.target.value,
        },
        id,
        configScope,
      })
  }

  const handleVendorsChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setVendor(String(value))
    const booleanValue = value === "true"
    value &&
      update({
        viewIntermediary: booleanValue,
        id,
        configScope,
      })
  }

  const handleBlur = (newClr: string) => {
    id &&
      newClr &&
      update({
        general: {
          borderColor: newClr,
        },
        id,
        configScope,
      })
  }

  useEffect(() => {
    data?.borderColor && setColor(data.borderColor)
    data && setValue(data)
    setVendor(viewIntermediaryString)
  }, [data, viewIntermediaryString])

  return (
    <div className={styles.container}>
      <h4 className="BodyBody-1BlackBold">{t("mba.general-settings.title")}</h4>
      <div className={styles.wrapper}>
        <div className={styles.column}>
          {inputs.map((input, idx) => {
            return (
              <Fragment key={`input_${idx}`}>
                <div className={styles.row}>
                  <h4
                    className={cx(
                      "BodyBody-1BlackRegular",
                      styles.colorPickerWrapper,
                    )}
                  >
                    {t(`mba.general-settings.${input.name}`)}
                  </h4>
                  {input.name !== "borderColor" ? (
                    <div className={styles.textSelector}>
                      <Options
                        onChange={handleChange}
                        options={input.options}
                        value={value[input.name as keyof GeneralMbaConfig]}
                        key={input.name}
                        name={input.name}
                        className={cx(
                          styles.font,
                          {
                            [styles.selector]: input.name === "fontSize",
                          },
                          { [styles.selector]: input.name === "borderSize" },
                        )}
                        disabled={disabled}
                      />
                    </div>
                  ) : (
                    <ColorPickerField
                      key={input.name}
                      name={input.name}
                      handlePicker={handleOpen}
                      open={open}
                      color={color}
                      setColor={setColor}
                      handleBlur={handleBlur}
                      disabled={disabled}
                    />
                  )}
                </div>
              </Fragment>
            )
          })}
        </div>
        <div className={styles.rowVendors}>
          <h4 className={cx("BodyBody-1BlackRegular", styles.vendorsText)}>
            {t(`mba.general-settings.intermediaryVendors`)}
          </h4>
          <Options
            onChange={handleVendorsChange}
            options={intermediaryData}
            value={vendor}
            key={"intermediaryVendors"}
            name={"intermediaryVendors"}
            className={styles.intermediaryVendors}
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  )
}

const styles = {
  container: css`
    display: flex;
    min-width: 100%;
    flex-direction: column;
  `,
  wrapper: css`
    display: flex;
    flex-direction: row;
  `,
  column: css`
    display: flex;
    flex-direction: column;
  `,
  row: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 30vw;
    padding-top: var(--spacing-xxl-2);
  `,
  rowVendors: css`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    width: 50vw;
    padding-top: var(--spacing-xxl-2);
  `,
  intermediaryVendors: css`
    max-width: 198px;
  `,
  font: css`
    max-width: 148px;
  `,
  selector: css`
    max-width: var(--spacing-xxl-7);
  `,
  textSelector: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 330px;
  `,
  colorPickerWrapper: css`
    display: flex;
    align-items: center;
    flex-direction: row;
    min-width: 120px;
  `,
  vendorsText: css`
    display: flex;
    align-items: center;
    flex-direction: row;
    min-width: 140px;
    padding: 6px var(--spacing-m) 0 0;
  `,
}
